import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CodeBlockIcon } from "@/components/UI/CodeBlockIcon";
import { useClipboardCopy } from "@/hooks/useClipboardCopy";
import { subscribeToApiErrors } from "@/services/apiErrorNotifications";
import "./ApiErrorNotifications.css";

interface ApiErrorNotification {
  id: number;
  message: string;
}

const NOTIFICATION_DURATION_MS = 5000;

const COPY_LABEL_KEYS = {
  copied: "a11y.copiedToClipboard",
  failed: "a11y.copyFailedToClipboard",
  ready: "a11y.copyToClipboard",
} as const;

// Errors reported in the same millisecond must still get distinct ids, so the
// counter runs independently of the clock.
let lastNotificationId = 0;

export function ApiErrorNotifications() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<readonly ApiErrorNotification[]>([]);

  useEffect(() => subscribeToApiErrors((message) => {
    lastNotificationId += 1;
    const id = lastNotificationId;
    setNotifications((current) => [...current, { id, message }]);
    window.setTimeout(() => {
      setNotifications((current) => current.filter((notification) => notification.id !== id));
    }, NOTIFICATION_DURATION_MS);
  }), []);

  return (
    <div
      className="api-error-notifications"
      aria-live="assertive"
      aria-label={t("apiErrors.label")}
    >
      {notifications.map((notification) => (
        <ApiErrorNotificationItem
          key={notification.id}
          message={notification.message}
          onDismiss={() => setNotifications((current) => current.filter((item) => item.id !== notification.id))}
        />
      ))}
    </div>
  );
}

// Every notification owns its copy state, so the clipboard hook cannot live in
// the loop that draws them.
function ApiErrorNotificationItem({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const { copy, status } = useClipboardCopy();

  const copyLabelKey = status === "idle" ? COPY_LABEL_KEYS.ready : COPY_LABEL_KEYS[status];

  return (
    <div className="api-error-notification" role="alert">
      <span>{message}</span>
      <div className="api-error-notification__actions">
        <button
          type="button"
          className="api-error-notification__copy"
          aria-label={t(copyLabelKey)}
          data-copy-status={status}
          onClick={() => void copy(message)}
        >
          <CodeBlockIcon name={status === "copied" ? "check" : "copy"} />
        </button>
        <button
          type="button"
          aria-label={t("apiErrors.dismiss")}
          onClick={onDismiss}
        >
          ×
        </button>
      </div>
    </div>
  );
}
